import { css } from '@emotion/react';
import { useMemo } from 'react';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { createColor } from '~/_shared/components/colorPicker/colorPicker.helpers';
import type { ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import { useTranslation } from '~/_shared/utils/hooks';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { LineColorComponent } from '../lineColor/lineColor.component';
import { LineWidthComponent } from '../lineWidth/lineWidth.component';
import {
  updateBoundaryTerritoryGroupLineColor, updateBoundaryTerritoryGroupLineWidth,
} from './boundaryTerritoryGroupUpdates.helpers';

const rightSideStyle = css({
  boxSizing: 'border-box',
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: 16,
  minWidth: 0,
});

const lineColorWrapperStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
});

const lineSettingsWrapperStyle = css({
  alignItems: 'center',
  cursor: 'default',
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
});

const checkboxContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
  padding: '4px 0',
  width: '100%',
});

export const checkboxStyle = css({
  textTransform: 'uppercase',
  fontSize: '12px',
});

type BoundaryCommonSettingsToggleProps = Readonly<{
  ignoreFilters: boolean;
  boundariesAsTerritories: boolean;
  hideWithoutData: boolean;
  hideFillWhenInactive: boolean;
  disableMapInteraction: boolean;

  onToggleBoundariesAsTerritories?: () => void;
  onToggleHideFillWhenInactive?: () => void;
  onToggleHideAreasWithoutData?: () => void;
  onToggleIgnoreFilters?: () => void;
  onToggleDisableMapInteraction?: () => void;
}>;

export type CommonSettingsProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  isBoundaryTerritoryGroupCustom: boolean;
  togglesProps: BoundaryCommonSettingsToggleProps;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
}>;

export const CommonSettingsComponent = ({
  boundaryTerritoryGroup,
  isBoundaryTerritoryGroupCustom,
  onBoundaryTerritoryGroupChange,
  togglesProps,
}: CommonSettingsProps) => {
  const [t] = useTranslation();
  const lineWidth = boundaryTerritoryGroup.settings.style.lineWidth;

  const lineColor = useMemo(() => createColor(boundaryTerritoryGroup.settings.style.lineColor)
    , [boundaryTerritoryGroup.settings.style.lineColor]);

  const handleChangeLineWidth = (width: number) => {
    onBoundaryTerritoryGroupChange(
      updateBoundaryTerritoryGroupLineWidth(boundaryTerritoryGroup, width)
    );
  };

  const handleChangeLineColor = (color: ColorResult) => {
    onBoundaryTerritoryGroupChange(
      updateBoundaryTerritoryGroupLineColor(boundaryTerritoryGroup, color)
    );
  };

  return (
    <div css={rightSideStyle}>
      <div css={lineSettingsWrapperStyle}>
        <LineWidthComponent
          lineWidth={lineWidth}
          onLineWidthChange={handleChangeLineWidth}
        />

        <div css={lineColorWrapperStyle}>
          <LineColorComponent
            lineColor={lineColor}
            onLineColorChange={handleChangeLineColor}
          />
        </div>
      </div>
      <div>
        {togglesProps.onToggleIgnoreFilters && (
          <div css={checkboxContainerStyle}>
            <CheckboxComponent
              isChecked={togglesProps.ignoreFilters}
              checkedSetter={togglesProps.onToggleIgnoreFilters}
              text={t('Ignore Filters')}
              textStyle={checkboxStyle}
            />
          </div>
        )}

        {togglesProps.onToggleBoundariesAsTerritories && (
          <div css={checkboxContainerStyle}>
            <CheckboxComponent
              isChecked={togglesProps.boundariesAsTerritories}
              checkedSetter={togglesProps.onToggleBoundariesAsTerritories}
              text={t('Combine Groups')}
              textStyle={checkboxStyle}
            />
          </div>
        )}

        {togglesProps.onToggleHideAreasWithoutData && (
          <div css={checkboxContainerStyle}>
            <CheckboxComponent
              isChecked={togglesProps.hideWithoutData}
              checkedSetter={togglesProps.onToggleHideAreasWithoutData}
              text={t('Hide Areas w/o Data')}
              textStyle={checkboxStyle}
            />
          </div>
        )}

        {togglesProps.onToggleHideFillWhenInactive && (
          <div css={checkboxContainerStyle}>
            <CheckboxComponent
              isChecked={!togglesProps.hideFillWhenInactive}
              checkedSetter={togglesProps.onToggleHideFillWhenInactive}
              text={t('Show Fill When Inactive')}
              textStyle={checkboxStyle}
            />
          </div>
        )}

        {togglesProps.onToggleDisableMapInteraction && (
          <div css={checkboxContainerStyle}>
            <CheckboxComponent
              isChecked={togglesProps.disableMapInteraction}
              checkedSetter={togglesProps.onToggleDisableMapInteraction}
              text={t(isBoundaryTerritoryGroupCustom ? 'boundarySettings.mapInteractionDisabled.territory' : 'boundarySettings.mapInteractionDisabled.boundary')}
              textStyle={checkboxStyle}
            />
          </div>
        )}
      </div>
    </div>
  );
};
